import * as React from 'react';
import * as styles from "./Smartmenu.css";
import { inject, observer } from 'mobx-react';
import { NavigatorStore } from './../stores';
import { Helper } from '../../../Helper';
import { IAIcon } from '../../icon/Icon';
import { ITopItem } from '../../../interfaces/ITopItem';
import { LocalizationService } from '../../../services/LocalizationService';
import "../../../styles/BasicIA.css";
import { ENTER_KEY } from '../../../global/Constants';
import { LoggingService } from '../../../services/LoggingService';
import { IAConditionalTooltipWrapper } from '../../tooltip/ConditionalTooltipWrapper';
import { NavigatorTargetingTooltip } from '../megamenu/targetingTooltip/TargetingTooltop';
import { IASpinner } from '../../spinner/Spinner';
import { IConfig } from '../../../interfaces/IConfig';
import { IAMessageBar } from '../../messageBar/MessageBar';

declare global {
  interface Window { currentUserLoginName: string }
}

export interface ISmartmenuProps {
  navigatorStore?: NavigatorStore;
}

export interface ISmartmenuState {
  linkCopied?: boolean;
  cursorX?: number;
  cursorY?: number;
}

@inject('navigatorStore')
@observer
export class Smartmenu extends React.Component<ISmartmenuProps, ISmartmenuState> {
  private readonly constructorName = "Smartmenu";

  constructor(props) {
    super(props);
    this.state = {
      linkCopied: false
    }
  }

  public componentDidMount(): void {
    window.addEventListener('popstate', () => {
      this.showSmartmenuPanel();
    });
    this.showSmartmenuPanel();
  }

  private showSmartmenuPanel(): void {
    if (location.hash) {
      let clearURL = true;
      this.props.navigatorStore.navigator?.smartmenu?.elements?.forEach((element: ITopItem) => {
        const id = location.hash.replace("#", "");
        if (element?.config?.id === id) {
          switch (element.type) {
            case "phonebook":
              this.loadPhonebook(element.config);
              break;
            case "targetedPreview":
              this.loadTargetedPreview(element.config);
              break;
            case "peoplefinder":
              this.loadPeoplefinder(element.config);
              break;
            case "event":
              this.loadEvent(element.config);
              break;
            case "contentcreator":
              this.loadContentCreator(element.config);
              break;
            case "ai":
              this.props.navigatorStore.selectedAiAssistent = element.config.instance;
              break;
            case "pagetranslation":
              this.props.navigatorStore.selectedTranslationPage = !this.props.navigatorStore.selectedTranslationPage;
              break;
            case "celebration":
              switch (element.config.subType) {
                case "snow":
                  this.props.navigatorStore.showSnowPopup = !this.props.navigatorStore.showSnowPopup;
                  break;
                case "confetti":
                  this.props.navigatorStore.showConfettiPopup = !this.props.navigatorStore.showConfettiPopup;
                  break;
                case "easterconfetti":
                  this.props.navigatorStore.showEasterConfettiPopup = !this.props.navigatorStore.showEasterConfettiPopup;
                  break;
                case "snhalloweenconfettiow":
                  this.props.navigatorStore.showHalloweenConfettiPopup = !this.props.navigatorStore.showHalloweenConfettiPopup;
                  break;
                default:
                  break;
              }
              this.props.navigatorStore.hasChanged = Helper.getRandomStringKey();
              this.props.navigatorStore.checkForChanges();
              break;
            default:
              clearURL = false;
              break;
          }
          if (clearURL) {
            setTimeout(() => {
              window.history.pushState('', '', location.href.split("#")[0]);
            }, 500);
          }
        }
      });
    }
  }

  private getTargetingInfo(index: number, seperator?: string): string {
    return this.props.navigatorStore.GetUserGroupTargetingForNavigatorAnalytics(this.props.navigatorStore.navigator.smartmenu.elements[index]?.targeting, this.props.navigatorStore.applicationSettings?.applicationSettings, seperator);
  }

  private loadPhonebook(config: IConfig): void {
    this.props.navigatorStore.selectedPhonebook = config;
    if (this.props.navigatorStore.isShowingMegamenu) {
      this.props.navigatorStore.toggleMegamenu();
    }
    document.body.style.overflowY = 'hidden';
    document.body.style.height = '100vh';
  }

  private loadTargetedPreview(config: IConfig): void {
    this.props.navigatorStore.isShowingMegamenu = false;
    this.props.navigatorStore.selectTopMenuItem(undefined);
    this.props.navigatorStore.getTopItems();
    this.props.navigatorStore.showTargetPreviewPanelWithTitle = config.title;
  }

  private loadPeoplefinder(config: IConfig): void {
    this.props.navigatorStore.selectedPeopleFinder = config;
    if (this.props.navigatorStore.isShowingMegamenu) {
      this.props.navigatorStore.toggleMegamenu();
    }
    document.body.style.overflowY = 'hidden';
    document.body.style.height = '100vh';
  }

  private loadEvent(config: IConfig): void {
    this.props.navigatorStore.selectedEvent = config;
    if (this.props.navigatorStore.isShowingMegamenu) {
      this.props.navigatorStore.toggleMegamenu();
    }
    document.body.style.overflowY = 'hidden';
    document.body.style.height = '100vh';
  }

  private loadContentCreator(config: IConfig): void {
    this.props.navigatorStore.selectedContentCreator = config;
    if (this.props.navigatorStore.isShowingMegamenu) {
      this.props.navigatorStore.toggleMegamenu();
    }
    document.body.style.overflowY = 'hidden';
    document.body.style.height = '100vh';
  }

  private copyLink(event, id: string): void {
    event.preventDefault();
    event.persist();
    let url = location.href;
    if (url.includes("#")) {
      url = url.split("#")[0];
    }
    const navigator: any = window.navigator;
    navigator.clipboard.writeText(`${url}#${id}`).then(() => {
      this.setState({ linkCopied: true, cursorX: event.clientX, cursorY: event.clientY });
      setTimeout(() => {
        this.setState({ linkCopied: false });
      }, 1000);
    }, (err) => {
      console.error('Async: Could not copy text: ', err);
    });
  }

  render() {
    let smartMenuItems: JSX.Element[] = [];
    let design = this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design : undefined;
    let addButton = (
      <div
        key={`smartMenuItemAddNewButton`}
        className={styles.topItemAddNewButton}
        style={{
          backgroundColor: this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
        }}
        onClick={() => {
          if (!(this.props.navigatorStore.isInEditMode && this.props.navigatorStore.checkedOut) && !this.props.navigatorStore.isShowingAnalytics) {
            this.props.navigatorStore.selectedTopMenuItemIndex = undefined;
            this.props.navigatorStore.addTopItemToSmartmenu();
          }
        }}
      >
        <div className={styles.topItemAddNewButtonIcon}>
          <IAIcon
            title={"Add"}
            size={22}
            color={this.props.navigatorStore.navigator.design.topItems.textColor}
            style={{
              marginLeft: -2
            }}
          />
        </div>
        <div
          className={styles.topItemAddNewButtonText}
          style={{
            color: this.props.navigatorStore.navigator.design.topItems.textColor
          }}
        >
          {new LocalizationService().strings.Navigator_Megamenu_AddItem}
        </div>
      </div>
    );
    if (this.props.navigatorStore.navigator != undefined && this.props.navigatorStore.navigator.smartmenu && this.props.navigatorStore.navigator.smartmenu.elements && this.props.navigatorStore.numberOfElementsInSmartmenu != undefined) {
      this.props.navigatorStore.navigator.smartmenu.elements.forEach((element: ITopItem, index) => {
        const targetingInfo = this.props.navigatorStore.isInEditMode && this.getTargetingInfo(index, "<br />");
        if (this.props.navigatorStore.isShowingAdditionalSmartmenuItems || index < (this.props.navigatorStore.isMobile && this.props.navigatorStore.numberOfElementsInSmartmenu > 5 ? 5 : this.props.navigatorStore.numberOfElementsInSmartmenu)) {
          if (element != undefined) {
            let randomID = Helper.getRandomStringKey();
            let heatMapColor = this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks ? this.props.navigatorStore.getHeatMapColorHSL(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks) : "";
            let heatMapBoxShadow = this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks ? `0px 0px 10px 8px ${this.props.navigatorStore.navigator.design.menuBar.backgroundColor ?? ""} inset` : "";
            switch (element.type) {
              case "phonebook":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          if (this.props.navigatorStore.selectedPhonebook == undefined || this.props.navigatorStore.selectedPhonebook.siteCollection != element.config.siteCollection) {
                            this.loadPhonebook(element.config);
                            LoggingService.trackEvent(this.constructorName, {
                              ActionType: 'LinkClick',
                              Targeting: this.getTargetingInfo(index),
                              Type: 'SmartMenuItem',
                              Title: !element.config.title ? element.config.url : element.config.title,
                              ItemType: element.type,
                              Url: element.config.url,
                              Tenant: this.props.navigatorStore.tenant,
                              Instance: this.props.navigatorStore.instance,
                              UserId: this.props.navigatorStore.token,
                              Version: this.props.navigatorStore.navigator.version.id,
                              PageUrl: window.location.pathname.toLowerCase(),
                              SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                            });
                          } else {
                            this.props.navigatorStore.selectedPhonebook = undefined;
                            document.body.style.overflowY = "auto";
                            document.body.style.height = "inherit";
                          }
                          this.props.navigatorStore.selectedEvent = undefined;
                          this.props.navigatorStore.selectedContentCreator = undefined;
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                }
                break;
              // Targeted Preview
              case "targetedpreview":
                if (this.props.navigatorStore.checktargetedPreviewIds(element) || this.props.navigatorStore.targetedPreviewIds?.length > 0) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          this.loadTargetedPreview(element.config);
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                      {this.props.navigatorStore.targetedPreviewIds.length > 0 &&
                        <div
                          className={styles.IA_targetedPreviewDot}
                        />
                      }
                    </div>
                  );
                }
                break;
              // People Finder
              case "peoplefinder":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          if (this.props.navigatorStore.selectedPeopleFinder == undefined || this.props.navigatorStore.selectedPeopleFinder.instance?.instanceName != element.config.instance?.instanceName) {
                            this.loadPeoplefinder(element.config);
                            LoggingService.trackEvent(this.constructorName, {
                              ActionType: 'LinkClick',
                              Targeting: this.getTargetingInfo(index),
                              Type: 'SmartMenuItem',
                              Title: !element.config.title ? element.config.url : element.config.title,
                              ItemType: element.type,
                              Url: element.config.url,
                              Tenant: this.props.navigatorStore.tenant,
                              Instance: this.props.navigatorStore.instance,
                              UserId: this.props.navigatorStore.token,
                              Version: this.props.navigatorStore.navigator.version.id,
                              PageUrl: window.location.pathname.toLowerCase(),
                              SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                            });
                          } else {
                            this.props.navigatorStore.selectedPeopleFinder = undefined;
                            document.body.style.overflowY = "auto";
                            document.body.style.height = "inherit";
                          }
                          this.props.navigatorStore.selectedPhonebook = undefined;
                          this.props.navigatorStore.selectedContentCreator = undefined;
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                }
                break;
              case "event":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      key={"smartmenuButton_" + index}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          if (this.props.navigatorStore.selectedEvent == undefined || this.props.navigatorStore.selectedEvent.instance.instanceName != element.config.instance.instanceName) {
                            this.loadEvent(element.config);
                            LoggingService.trackEvent(this.constructorName, {
                              ActionType: 'LinkClick',
                              Targeting: this.getTargetingInfo(index),
                              Type: 'SmartMenuItem',
                              Title: !element.config.title ? element.config.url : element.config.title,
                              ItemType: element.type,
                              Url: element.config.url,
                              Tenant: this.props.navigatorStore.tenant,
                              Instance: this.props.navigatorStore.instance,
                              UserId: this.props.navigatorStore.token,
                              Version: this.props.navigatorStore.navigator.version.id,
                              PageUrl: window.location.pathname.toLowerCase(),
                              SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                            });
                          } else {
                            this.props.navigatorStore.selectedEvent = undefined;
                            document.body.style.overflowY = "auto";
                            document.body.style.height = "inherit";
                          }
                          this.props.navigatorStore.selectedPhonebook = undefined;
                          this.props.navigatorStore.selectedContentCreator = undefined;
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                }
                break;
              case "contentcreator":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          if (this.props.navigatorStore.selectedContentCreator == undefined || this.props.navigatorStore.selectedContentCreator.instance.instanceName != element.config.instance.instanceName) {
                            this.loadContentCreator(element.config);
                            LoggingService.trackEvent(this.constructorName, {
                              ActionType: 'LinkClick',
                              Targeting: this.getTargetingInfo(index),
                              Type: 'SmartMenuItem',
                              Title: !element.config.title ? element.config.url : element.config.title,
                              ItemType: element.type,
                              Url: element.config.url,
                              Tenant: this.props.navigatorStore.tenant,
                              Instance: this.props.navigatorStore.instance,
                              UserId: this.props.navigatorStore.token,
                              Version: this.props.navigatorStore.navigator.version.id,
                              PageUrl: window.location.pathname.toLowerCase(),
                              SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                            });
                          } else {
                            this.props.navigatorStore.selectedContentCreator = undefined;
                            document.body.style.overflowY = "auto";
                            document.body.style.height = "inherit";
                          }
                          this.props.navigatorStore.selectedPhonebook = undefined;
                          this.props.navigatorStore.selectedEvent = undefined;
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                }
                break;
              case "code":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      key={"smartmenuButton_" + index}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          LoggingService.trackEvent(this.constructorName, {
                            ActionType: 'LinkClick',
                            Targeting: this.getTargetingInfo(index),
                            Type: 'SmartMenuItem',
                            Title: !element.config.title ? element.config.url : element.config.title,
                            ItemType: element.type,
                            Url: element.config.url,
                            Tenant: this.props.navigatorStore.tenant,
                            Instance: this.props.navigatorStore.instance,
                            UserId: this.props.navigatorStore.token,
                            Version: this.props.navigatorStore.navigator.version.id,
                            PageUrl: window.location.pathname.toLowerCase(),
                            SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                          });
                          window.currentUserLoginName = this.props.navigatorStore.user?.userPrincipalName;
                          var headID = document.getElementsByTagName("head")[0];
                          var customScript = document.createElement("script");
                          customScript.type = "text/javascript";
                          customScript.innerHTML = element.config.code;
                          headID.appendChild(customScript);
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                  break;
                }
              case "celebration":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  if (!this.props.navigatorStore.isMobile) {
                    if (element.config.subType === "snow") {
                      smartMenuItems.push(
                        <div
                          id={randomID}
                          tabIndex={1}
                          onContextMenu={(event) => this.copyLink(event, element.config.id)}
                          className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                          style={{
                            backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                            boxShadow: heatMapBoxShadow,
                            opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                            float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                          }}
                          draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                          onDragStart={() => {
                            this.props.navigatorStore.setDraggedTopItem(index);
                          }}
                          onDragEnd={() => {
                            this.props.navigatorStore.draggedTopItemIndex = undefined;
                          }}
                          title={element.config.title}
                          onClick={() => {
                            if (this.props.navigatorStore.isInEditMode) {
                              if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                                this.props.navigatorStore.checkForNewContent();
                                this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                                this.props.navigatorStore.topItemIndexToEdit = undefined;
                                this.props.navigatorStore.toggleTopItemSettings();
                              }
                              this.props.navigatorStore.verticalMenuExpanded = true;
                            } else {
                              LoggingService.trackEvent(this.constructorName, {
                                ActionType: 'LinkClick',
                                Targeting: this.getTargetingInfo(index),
                                Type: 'SmartMenuItem',
                                Title: !element.config.title ? element.config.url : element.config.title,
                                ItemType: element.type,
                                Url: element.config.url,
                                Tenant: this.props.navigatorStore.tenant,
                                Instance: this.props.navigatorStore.instance,
                                UserId: this.props.navigatorStore.token,
                                Version: this.props.navigatorStore.navigator.version.id,
                                PageUrl: window.location.pathname.toLowerCase(),
                                SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                              });

                              this.props.navigatorStore.showSnowPopup = !this.props.navigatorStore.showSnowPopup;
                              this.props.navigatorStore.hasChanged = Helper.getRandomStringKey();
                              this.props.navigatorStore.checkForChanges();
                            }
                          }}
                          onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                            if (event.key === ENTER_KEY) {
                              document.getElementById(randomID).click();
                            }
                          }}
                        >
                          <IAConditionalTooltipWrapper
                            condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                            tooltipContent={
                              <div>
                                {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                                ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                              </div>
                            }
                            wrapper={(content) =>
                              <>{content}</>
                            }
                          >
                            <IAIcon
                              title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                              size={22}
                              color={design.menuBar.iconColor}
                              style={{
                                paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                                boxSizing: "border-box",
                                float: "left"
                              }}
                            />
                          </IAConditionalTooltipWrapper>
                          {targetingInfo &&
                            <NavigatorTargetingTooltip
                              highlightColor={this.props.navigatorStore.highlightColor}
                              targetingInfo={targetingInfo}
                            />
                          }
                          {
                            this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                            <div
                              className={styles.smartmenuButtonText}
                              style={{
                                color: this.props.navigatorStore.navigator.design.topItems.textColor,
                                letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                                textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                                fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                                fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                              }}
                            >
                              {element.config.title}
                            </div>

                          }
                        </div>
                      );
                    } else if (element.config.subType === "confetti") {
                      smartMenuItems.push(
                        <div
                          id={randomID}
                          tabIndex={1}
                          onContextMenu={(event) => this.copyLink(event, element.config.id)}
                          className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                          style={{
                            backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                            boxShadow: heatMapBoxShadow,
                            opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                            float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                          }}
                          draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                          onDragStart={() => {
                            this.props.navigatorStore.setDraggedTopItem(index);
                          }}
                          onDragEnd={() => {
                            this.props.navigatorStore.draggedTopItemIndex = undefined;
                          }}
                          title={element.config.title}
                          onClick={() => {
                            if (this.props.navigatorStore.isInEditMode) {
                              if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                                this.props.navigatorStore.checkForNewContent();
                                this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                                this.props.navigatorStore.topItemIndexToEdit = undefined;
                                this.props.navigatorStore.toggleTopItemSettings();
                              }
                              this.props.navigatorStore.verticalMenuExpanded = true;
                            } else {
                              LoggingService.trackEvent(this.constructorName, {
                                ActionType: 'LinkClick',
                                Targeting: this.getTargetingInfo(index),
                                Type: 'SmartMenuItem',
                                Title: !element.config.title ? element.config.url : element.config.title,
                                ItemType: element.type,
                                Url: element.config.url,
                                Tenant: this.props.navigatorStore.tenant,
                                Instance: this.props.navigatorStore.instance,
                                UserId: this.props.navigatorStore.token,
                                Version: this.props.navigatorStore.navigator.version.id,
                                PageUrl: window.location.pathname.toLowerCase(),
                                SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                              });

                              this.props.navigatorStore.showConfettiPopup = !this.props.navigatorStore.showConfettiPopup;
                              this.props.navigatorStore.hasChanged = Helper.getRandomStringKey();
                              this.props.navigatorStore.checkForChanges();
                            }
                          }}
                          onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                            if (event.key === ENTER_KEY) {
                              document.getElementById(randomID).click();
                            }
                          }}
                        >
                          <IAConditionalTooltipWrapper
                            condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                            tooltipContent={
                              <div>
                                {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                                ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                              </div>
                            }
                            wrapper={(content) =>
                              <>{content}</>
                            }
                          >
                            <IAIcon
                              title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                              size={22}
                              color={design.menuBar.iconColor}
                              style={{
                                paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                                boxSizing: "border-box",
                                float: "left"
                              }}
                            />
                          </IAConditionalTooltipWrapper>
                          {targetingInfo &&
                            <NavigatorTargetingTooltip
                              highlightColor={this.props.navigatorStore.highlightColor}
                              targetingInfo={targetingInfo}
                            />
                          }
                          {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                            <div
                              className={styles.smartmenuButtonText}
                              style={{
                                color: this.props.navigatorStore.navigator.design.topItems.textColor,
                                letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                                textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                                fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                                fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                              }}
                            >
                              {element.config.title}
                            </div>
                          }
                        </div>
                      );
                    } else if (element.config.subType === "easterconfetti") {
                      smartMenuItems.push(
                        <div
                          id={randomID}
                          tabIndex={1}
                          onContextMenu={(event) => this.copyLink(event, element.config.id)}
                          className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                          key={"smartmenuButton_" + index}
                          style={{
                            backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                            boxShadow: heatMapBoxShadow,
                            opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                            float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                          }}
                          draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                          onDragStart={() => {
                            this.props.navigatorStore.setDraggedTopItem(index);
                          }}
                          onDragEnd={() => {
                            this.props.navigatorStore.draggedTopItemIndex = undefined;
                          }}
                          title={element.config.title}
                          onClick={() => {
                            if (this.props.navigatorStore.isInEditMode) {
                              if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                                this.props.navigatorStore.checkForNewContent();
                                this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                                this.props.navigatorStore.topItemIndexToEdit = undefined;
                                this.props.navigatorStore.toggleTopItemSettings();
                              }
                              this.props.navigatorStore.verticalMenuExpanded = true;
                            } else {
                              LoggingService.trackEvent(this.constructorName, {
                                ActionType: 'LinkClick',
                                Targeting: this.getTargetingInfo(index),
                                Type: 'SmartMenuItem',
                                Title: !element.config.title ? element.config.url : element.config.title,
                                ItemType: element.type,
                                Url: element.config.url,
                                Tenant: this.props.navigatorStore.tenant,
                                Instance: this.props.navigatorStore.instance,
                                UserId: this.props.navigatorStore.token,
                                Version: this.props.navigatorStore.navigator.version.id,
                                PageUrl: window.location.pathname.toLowerCase(),
                                SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                              });

                              this.props.navigatorStore.showEasterConfettiPopup = !this.props.navigatorStore.showEasterConfettiPopup;
                              this.props.navigatorStore.hasChanged = Helper.getRandomStringKey();
                              this.props.navigatorStore.checkForChanges();
                            }
                          }}
                          onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                            if (event.key === ENTER_KEY) {
                              document.getElementById(randomID).click();
                            }
                          }}
                        >
                          <IAConditionalTooltipWrapper
                            condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                            tooltipContent={
                              <div>
                                {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                                ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                              </div>
                            }
                            wrapper={(content) =>
                              <>{content}</>
                            }
                          >
                            <IAIcon
                              title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                              size={22}
                              color={design.menuBar.iconColor}
                              style={{
                                paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                                boxSizing: "border-box",
                                float: "left"
                              }}
                            />
                          </IAConditionalTooltipWrapper>
                          {targetingInfo &&
                            <NavigatorTargetingTooltip
                              highlightColor={this.props.navigatorStore.highlightColor}
                              targetingInfo={targetingInfo}
                            />
                          }
                          {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                            <div
                              className={styles.smartmenuButtonText}
                              style={{
                                color: this.props.navigatorStore.navigator.design.topItems.textColor,
                                letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                                textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                                fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                                fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                              }}
                            >
                              {element.config.title}
                            </div>
                          }
                        </div>
                      );
                    } else if (element.config.subType === "halloweenconfetti") {
                      smartMenuItems.push(
                        <div
                          id={randomID}
                          tabIndex={1}
                          onContextMenu={(event) => this.copyLink(event, element.config.id)}
                          className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                          key={"smartmenuButton_" + index}
                          style={{
                            backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                            boxShadow: heatMapBoxShadow,
                            opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                            float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                          }}
                          draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                          onDragStart={() => {
                            this.props.navigatorStore.setDraggedTopItem(index);
                          }}
                          onDragEnd={() => {
                            this.props.navigatorStore.draggedTopItemIndex = undefined;
                          }}
                          title={element.config.title}
                          onClick={() => {
                            if (this.props.navigatorStore.isInEditMode) {
                              if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                                this.props.navigatorStore.checkForNewContent();
                                this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                                this.props.navigatorStore.topItemIndexToEdit = undefined;
                                this.props.navigatorStore.toggleTopItemSettings();
                              }
                              this.props.navigatorStore.verticalMenuExpanded = true;
                            } else {
                              LoggingService.trackEvent(this.constructorName, {
                                ActionType: 'LinkClick',
                                Targeting: this.getTargetingInfo(index),
                                Type: 'SmartMenuItem',
                                Title: !element.config.title ? element.config.url : element.config.title,
                                ItemType: element.type,
                                Url: element.config.url,
                                Tenant: this.props.navigatorStore.tenant,
                                Instance: this.props.navigatorStore.instance,
                                UserId: this.props.navigatorStore.token,
                                Version: this.props.navigatorStore.navigator.version.id,
                                PageUrl: window.location.pathname.toLowerCase(),
                                SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                              });

                              this.props.navigatorStore.showHalloweenConfettiPopup = !this.props.navigatorStore.showHalloweenConfettiPopup;
                              this.props.navigatorStore.hasChanged = Helper.getRandomStringKey();
                              this.props.navigatorStore.checkForChanges();
                            }
                          }}
                          onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                            if (event.key === ENTER_KEY) {
                              document.getElementById(randomID).click();
                            }
                          }}
                        >
                          <IAConditionalTooltipWrapper
                            condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                            tooltipContent={
                              <div>
                                {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                                ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                              </div>
                            }
                            wrapper={(content) =>
                              <>{content}</>
                            }
                          >
                            <IAIcon
                              title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                              size={22}
                              color={design.menuBar.iconColor}
                              style={{
                                paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                                boxSizing: "border-box",
                                float: "left"
                              }}
                            />
                          </IAConditionalTooltipWrapper>
                          {targetingInfo &&
                            <NavigatorTargetingTooltip
                              highlightColor={this.props.navigatorStore.highlightColor}
                              targetingInfo={targetingInfo}
                            />
                          }
                          {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                            <div
                              className={styles.smartmenuButtonText}
                              style={{
                                color: this.props.navigatorStore.navigator.design.topItems.textColor,
                                letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                                textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                                fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                                fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                              }}
                            >
                              {element.config.title}
                            </div>
                          }
                        </div>
                      );
                    }
                  }
                }
                break;

              // Clean URL
              // setTimeout(() => {
              //   const newURL = location.href.replace(location.hash, "");
              //   var obj = { Page: newURL };
              //   history.pushState(obj, "", newURL);
              // }, 500);
              case "ai":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={() => {
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          LoggingService.trackEvent(this.constructorName, {
                            ActionType: 'LinkClick',
                            Targeting: this.getTargetingInfo(index),
                            Type: 'SmartMenuItem',
                            Title: element.config.title,
                            ItemType: element.type,
                            Url: element.config.instance?.siteCollection,
                            Tenant: this.props.navigatorStore.tenant,
                            Instance: this.props.navigatorStore.instance,
                            UserId: this.props.navigatorStore.token,
                            Version: this.props.navigatorStore.navigator.version.id,
                            PageUrl: window.location.pathname.toLowerCase(),
                            SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                          });
                          this.props.navigatorStore.selectedAiAssistent = element.config.instance;
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                }
                break;
              case "pagetranslation":
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <div
                      id={randomID}
                      tabIndex={1}
                      onContextMenu={(event) => this.copyLink(event, element.config.id)}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                      title={element.config.title}
                      onClick={(event) => {
                        this.props.navigatorStore.translationIconRight = (event.target as HTMLElement).getBoundingClientRect().right;
                        if (this.props.navigatorStore.isInEditMode) {
                          if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                            this.props.navigatorStore.checkForNewContent();
                            this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                            this.props.navigatorStore.topItemIndexToEdit = undefined;
                            this.props.navigatorStore.toggleTopItemSettings();
                          }
                          this.props.navigatorStore.verticalMenuExpanded = true;
                        } else {
                          LoggingService.trackEvent(this.constructorName, {
                            ActionType: 'LinkClick',
                            Targeting: this.getTargetingInfo(index),
                            Type: 'SmartMenuItem',
                            Title: element.config.title,
                            ItemType: element.type,
                            Url: element.config.instance?.siteCollection,
                            Tenant: this.props.navigatorStore.tenant,
                            Instance: this.props.navigatorStore.instance,
                            UserId: this.props.navigatorStore.token,
                            Version: this.props.navigatorStore.navigator.version.id,
                            PageUrl: window.location.pathname.toLowerCase(),
                            SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                          });

                          this.props.navigatorStore.selectedTranslationPage = !this.props.navigatorStore.selectedTranslationPage;
                        }
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        {
                          this.props.navigatorStore.isTranslating ?
                            <IASpinner
                              color={design.menuBar.iconColor}
                              style={{
                                clear: "both",
                                float: "left",
                                width: "12px",
                                height: "12px",
                                marginTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 15 : 10,
                                marginLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                              }}
                            /> : <IAIcon
                              title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                              size={22}
                              color={design.menuBar.iconColor}
                              style={{
                                paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                                width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                                boxSizing: "border-box",
                                float: "left"
                              }}
                            />
                        }

                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </div>
                  );
                }
                break;
              default:
                if (this.props.navigatorStore.checktargetedPreviewIds(element)) {
                  smartMenuItems.push(
                    <a
                      id={randomID}
                      tabIndex={1}
                      className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
                      key={"smartmenuButton_" + index}
                      data-interception={(element.config.openInNewWindow || this.props.navigatorStore.loadLinksExternally) ? "off" : "on"}
                      target={(element.config.openInNewWindow || this.props.navigatorStore.loadLinksExternally) ? "_blank" : "_self"}
                      href={element.config.url != undefined && element.config.url != "" && !this.props.navigatorStore.isInEditMode ? element.config.url : undefined}
                      onClick={() => {
                        if (!(this.props.navigatorStore.isMobile && (element.config.openInNewWindow || this.props.navigatorStore.loadLinksExternally))) {
                          if (this.props.navigatorStore.isInEditMode) {
                            if (!this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.checkedOut && !this.props.navigatorStore.isShowingAnalytics) {
                              this.props.navigatorStore.checkForNewContent();
                              this.props.navigatorStore.smartmenuTopItemIndexToEdit = index;
                              this.props.navigatorStore.topItemIndexToEdit = undefined;
                              this.props.navigatorStore.toggleTopItemSettings();
                            }
                            this.props.navigatorStore.verticalMenuExpanded = true;
                          }
                        }

                        LoggingService.trackEvent(this.constructorName, {
                          ActionType: 'LinkClick',
                          Targeting: this.getTargetingInfo(index),
                          Type: 'SmartMenuItem',
                          Title: !element.config.title ? element.config.url : element.config.title,
                          ItemType: element.type,
                          Url: element.config.url,
                          Tenant: this.props.navigatorStore.tenant,
                          Instance: this.props.navigatorStore.instance,
                          UserId: this.props.navigatorStore.token,
                          Version: this.props.navigatorStore.navigator.version.id,
                          PageUrl: window.location.pathname.toLowerCase(),
                          SiteName: Helper.getSiteCollectionFromPath(window.location.pathname)?.toLowerCase() ?? "none",
                        });
                      }}
                      onKeyPress={(event: React.KeyboardEvent<HTMLAnchorElement>) => {
                        if (event.key === ENTER_KEY) {
                          document.getElementById(randomID).click();
                        }
                      }}
                      style={{
                        backgroundColor: this.props.navigatorStore.isShowingAnalytics ? heatMapColor : this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
                        boxShadow: heatMapBoxShadow,
                        opacity: this.props.navigatorStore.draggedTopItemIndex == index && this.props.navigatorStore.droppableTopItemSpotsAreShowing && !this.props.navigatorStore.isShowingAnalytics ? 0.5 : 1,
                        float: this.props.navigatorStore.isInEditMode ? "right" : "left",
                      }}
                      title={element.config.title}
                      draggable={this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing}
                      onDragStart={() => {
                        this.props.navigatorStore.setDraggedTopItem(index);
                      }}
                      onDragEnd={() => {
                        this.props.navigatorStore.draggedTopItemIndex = undefined;
                      }}
                    >
                      <IAConditionalTooltipWrapper
                        condition={this.props.navigatorStore.isShowingAnalytics && this.props.navigatorStore.doneSettingClicks}
                        tooltipContent={
                          <div>
                            {this.props.navigatorStore.getClickTooltipLocalizationString(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}
                            ({this.props.navigatorStore.getPercentageOfClickComparedToAllClicks(this.props.navigatorStore.clickFilter === 'Total' ? this.props.navigatorStore.navigator.smartmenu.elements[index].config.clicks : this.props.navigatorStore.navigator.smartmenu.elements[index].config.uniqueClicks)}%)
                          </div>
                        }
                        wrapper={(content) =>
                          <>{content}</>
                        }
                      >
                        <IAIcon
                          title={this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && element.config.icon == undefined ? "Link" : element.config.icon}
                          size={22}
                          color={design.menuBar.iconColor}
                          style={{
                            paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                            width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
                            boxSizing: "border-box",
                            float: "left"
                          }}
                        />
                      </IAConditionalTooltipWrapper>
                      {targetingInfo &&
                        <NavigatorTargetingTooltip
                          highlightColor={this.props.navigatorStore.highlightColor}
                          targetingInfo={targetingInfo}
                        />
                      }
                      {this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
                        <div
                          className={styles.smartmenuButtonText}
                          style={{
                            color: this.props.navigatorStore.navigator.design.topItems.textColor,
                            letterSpacing: this.props.navigatorStore.navigator.design.topItems.letterSpacing,
                            textTransform: this.props.navigatorStore.navigator.design.topItems.textTransform,
                            fontWeight: this.props.navigatorStore.navigator.design.topItems.fontWeight,
                            fontSize: this.props.navigatorStore.navigator.design.topItems.fontSize,
                          }}
                        >
                          {element.config.title}
                        </div>
                      }
                    </a>
                  );
                }
                break;
            }
          }
        }
      });
      // if (this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing) {
      //   smartMenuItems.push(
      //     <SmartmenuTopItemDroppable
      //       key={`smartmenuTopItemDroppable_${this.props.navigatorStore.navigator.megamenu.topItems.length}`}
      //       index={this.props.navigatorStore.navigator.megamenu.topItems.length}
      //     />
      //   )
      // }
      if (this.props.navigatorStore.isInEditMode && this.props.navigatorStore.isShowingAdditionalSmartmenuItems || this.props.navigatorStore.isInEditMode && this.props.navigatorStore.navigator.smartmenu.elements.length == 0) {
        if (!this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing) {
          if (this.props.navigatorStore.navigator.design.menuBar.layout == "vertical") {
            smartMenuItems.push(addButton);
          } else {
            smartMenuItems.unshift(addButton);
          }
        }
        if (this.props.navigatorStore.navigator.smartmenu.elements.length > 1 && !this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing) {
          let moveButton = (
            <div
              className={styles.topItemAddNewButton}
              key={`moveButton_${this.props.navigatorStore.navigator.smartmenu.elements.length}`}
              style={{
                backgroundColor: this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
              }}
              onClick={() => {
                if (!(this.props.navigatorStore.isInEditMode && this.props.navigatorStore.checkedOut) && !this.props.navigatorStore.isShowingAnalytics) {
                  if (this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing) {
                    this.props.navigatorStore.closeReorderSmartMenu();
                  } else {
                    this.props.navigatorStore.reorderSmartMenu();
                  }
                }
              }}
            >
              <div className={styles.topItemAddNewButtonIcon}>
                <IAIcon
                  title={"Move"}
                  size={22}
                  color={this.props.navigatorStore.navigator.design.topItems.textColor}
                />
              </div>
              <div
                className={styles.topItemAddNewButtonText}
                style={{
                  color: this.props.navigatorStore.navigator.design.topItems.textColor
                }}
              >
                {this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing ? new LocalizationService().strings.Navigator_Megamenu_DoneReordering : new LocalizationService().strings.Navigator_Megamen_ReorderTopItems}
              </div>
            </div>
          );
          if (this.props.navigatorStore.navigator.design.menuBar.layout == "vertical") {
            smartMenuItems.push(moveButton);
          } else {
            smartMenuItems.unshift(moveButton);
          }
        }
      }
      if (this.props.navigatorStore.showMoreSmartmenuTopItemsButton && !this.props.navigatorStore.isShowingAdditionalSmartmenuItems && this.props.navigatorStore.navigator.smartmenu.elements.length > 0) {
        let moreButton = (
          <div
            className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
            tabIndex={1}
            id="showMoreButton"
            style={{
              backgroundColor: this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.backgroundColor : undefined,
            }}
            onClick={() => {
              if (this.props.navigatorStore.showMoreHorizontalTopItems) {
                this.props.navigatorStore.showMoreHorizontalTopItems = false;
                this.props.navigatorStore.moreHorizontalTopItemsToDisplay = undefined;
                if (this.props.navigatorStore.droppableTopItemSpotsAreShowing) {
                  this.props.navigatorStore.hideDroppableTopItemSpots();
                  this.props.navigatorStore.selectTopMenuItem(undefined);
                }
                this.props.navigatorStore.getTopItems();
              }
              this.props.navigatorStore.isShowingAdditionalSmartmenuItems = !this.props.navigatorStore.isShowingAdditionalSmartmenuItems;
              this.props.navigatorStore.verticalMenuExpanded = true;
              this.props.navigatorStore.resize();
              this.forceUpdate();
            }}
            onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === ENTER_KEY) {
                document.getElementById("showMoreButton").click();
              }
            }}
          >
            <IAIcon
              title="More"
              size={22}
              color={design.menuBar.iconColor}
              style={{
                paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 13 : 8,
                width: 50,
                boxSizing: "border-box",
                float: "left"
              }}
            />
          </div>
        );
        if (this.props.navigatorStore.navigator.design.menuBar.layout == "vertical") {
          smartMenuItems.push(moreButton);
        } else {
          smartMenuItems.unshift(moreButton);
        }
      }
    } else {
      if (this.props.navigatorStore.navigator.design.menuBar.layout == "vertical") {
        smartMenuItems.push(addButton);
      } else {
        smartMenuItems.unshift(addButton);
      }
    }

    if (!this.props.navigatorStore.isInEditMode) {
      smartMenuItems.reverse();
    }

    if (this.props.navigatorStore.isShowingAdditionalSmartmenuItems && !this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing) {
      let closeButton = (
        <div
          id="closeSmartMenuButton"
          className={[styles.smartmenuButton, "IA_OnFocus"].join(" ")}
          tabIndex={1}
          style={{
            borderBottom: `1px solid ${this.props.navigatorStore.navigator.design.topItems.textColor}80`,
            backgroundColor: this.props.navigatorStore.navigator != undefined ? this.props.navigatorStore.navigator.design.menuBar.highlightColor : undefined,
          }}
          onClick={() => {
            this.props.navigatorStore.isShowingAdditionalSmartmenuItems = !this.props.navigatorStore.isShowingAdditionalSmartmenuItems;
            this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing = false;
            this.props.navigatorStore.resize();
            this.forceUpdate();
          }}
          onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === ENTER_KEY) {
              document.getElementById("closeSmartMenuButton").click();
            }
          }}
        >
          <IAIcon
            title="Close"
            size={22}
            color={design.menuBar.iconColor}
            style={{
              paddingTop: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 15 : 10,
              paddingLeft: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 14 : 10,
              width: this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? 50 : 40,
              boxSizing: "border-box",
              float: "left"
            }}
          />
        </div>
      );
      if (this.props.navigatorStore.navigator.design.menuBar.layout == "vertical") {
        smartMenuItems.push(closeButton);
      } else {
        smartMenuItems.unshift(closeButton);
      }
    }
    let rgbColor = Helper.convertHexToRgb(design != undefined && design.menuBar != undefined && design.menuBar.iconColor != undefined ? design.menuBar.iconColor : "#333333");
    let rgbBackgroundColor = Helper.convertHexToRgb(design != undefined && design.menuBar != undefined && design.menuBar.backgroundColor != undefined ? design.menuBar.backgroundColor : "#333333");
    return (
      <div
        className={this.props.navigatorStore.navigator != undefined && this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" ? styles.smartmenuVertical : styles.smartmenu}
        style={{
          width: this.props.navigatorStore.navigator != undefined && this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" && this.props.navigatorStore.verticalMenuExpanded ? 300 : this.props.navigatorStore.smartmenuWidth,
          height: this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing ? "auto" : this.props.navigatorStore.smartmenuHeight,
          backgroundColor: this.props.navigatorStore.navigator.design.menuBar.layout != "vertical" ? design.menuBar.backgroundColor : "tranparent",
          zIndex: this.props.navigatorStore.isShowingAnalytics ? undefined : 900006
        }}
      >
        {!this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing && this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
          <div style={{
            width: "100%",
            height: 20,
            backgroundImage: `linear-gradient(rgba(${rgbBackgroundColor.r},${rgbBackgroundColor.g},${rgbBackgroundColor.b},0), rgba(${rgbBackgroundColor.r},${rgbBackgroundColor.g},${rgbBackgroundColor.b},1))`,
            float: "left"
          }} />
        }
        {!this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing && this.props.navigatorStore.navigator.design.menuBar.layout == "vertical" &&
          <div style={{
            width: "100%",
            height: 1,
            marginTop: -1,
            backgroundColor: `rgba(${rgbColor.r},${rgbColor.g},${rgbColor.b},0.3)`,
            float: "left"
          }} />
        }
        {smartMenuItems}
        {this.state.linkCopied &&
          <div
            style={{
              color: "#777777",
              position: "fixed",
              fontSize: 11,
              boxSizing: "border-box",
              userSelect: "none",
              background: "#ffffff",
              padding: "4px 8px",
              borderRadius: 2,
              left: this.state.cursorX - 30,
              top: this.state.cursorY + 30,
            }}
          >
            {new LocalizationService().strings.Panel_NavigatorBar_LinkCopied}
          </div>
        }
        {this.props.navigatorStore.targetedPreviewIds?.length > 0 && !location.href?.toLowerCase().includes("intraactive-admin") &&
          <IAMessageBar
            type="information"
            content={new LocalizationService().strings.Navigator_TargetedPreviewMessageContent}
            icon="Info"
            showIcon={true}
            showCloseButton={false}
            highlightColor={this.props.navigatorStore.highlightColor}
            dismissContentMessage={new LocalizationService().strings.Navigator_TargetedPreviewMessageButten}
            onDismiss={() => {
              localStorage.removeItem("targetedPreviewIds");
              location.reload();
            }}
            style={{
              position: "fixed",
              zIndex: 2000000,
              left: this.props.navigatorStore.isMobile ? 0 : "calc(50vw - 250px)",
              width: this.props.navigatorStore.isMobile ? window.innerWidth : "500px",
              borderRadius: 5,
              padding: "5px 5px",
              marginBottom: 20,
              top: 88
            }}
          />
        }
      </div >
    )
  }


}